import { useFilterQuantity, useFilterSpices } from "@/hooks/useFilter";
import { useRef, useCallback, useMemo, useState, useEffect } from "react";
import { Units } from "./Units";
import { useSpices } from "@/hooks/useSpices";

interface QuantityProps {
  selectedSpice: number | null;
  min?: number;
  max?: number;
  step?: number;
  throttle?: number;
}

// Scroll-wheel hook: derives which value is centred in the viewport,
// shows it immediately as a preview, then commits after scrolling stops.
function useQuantityScroll(
  selectedSpice: number | null,
  committed: number,
  min: number,
  max: number,
  step: number,
  throttle: number
) {
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const adjustingRef = useRef(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [, setQuantity] = useFilterQuantity();
  const { updateSpice } = useSpices();

  const displayed = preview ?? committed;

  // Set top/bottom padding so item 0 and item max can reach the centre of the viewport.
  // With padding = containerHeight/2 - itemHeight/2, scrollTop maps cleanly to index:
  //   index = Math.round(scrollTop / itemHeight)
  //   scrollTop = index * itemHeight
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const itemHeight = (el.firstElementChild as HTMLElement)?.offsetHeight;
    if (!itemHeight) return;
    const padding = `${el.clientHeight / 2 - itemHeight / 2}px`;
    el.style.paddingTop = padding;
    el.style.paddingBottom = padding;
  }, []);

  const onScroll = useCallback(() => {
    if (!ref.current || !selectedSpice || adjustingRef.current) return;

    const el = ref.current;
    const itemHeight = (el.firstElementChild as HTMLElement)?.offsetHeight;
    if (!itemHeight) return;

    const index = Math.round(el.scrollTop / itemHeight);
    const value = Math.min(Math.max(min + index * step, min), max);

    setPreview(value);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (adjustingRef.current) return; // still animating to a new spice — don't commit
      if (value === 0) {
        updateSpice(selectedSpice, "remove");
      } else if (value !== committed) {
        setQuantity(value);
        updateSpice(selectedSpice, "set", value);
      }
      setPreview(null);
    }, value === 0 ? 400 : throttle);
  }, [selectedSpice, committed, min, max, step, throttle, setQuantity, updateSpice]);

  const [spices] = useFilterSpices();

  // When selected spice changes: lock the wheel, animate to that spice's quantity,
  // then unlock once the scroll settles
  useEffect(() => {
    if (!ref.current || !selectedSpice) return;
    const el = ref.current;
    const itemHeight = (el.firstElementChild as HTMLElement)?.offsetHeight;
    if (!itemHeight) return;

    const spiceQuantity = Math.round(spices.get(selectedSpice)?.quantity ?? min);
    const index = (spiceQuantity - min) / step;
    const scrollTop = index * itemHeight;

    const unlock = () => {
      adjustingRef.current = false;
      el.style.pointerEvents = "";
    };

    if (Math.round(el.scrollTop) === Math.round(scrollTop)) {
      unlock();
      return;
    }

    adjustingRef.current = true;
    el.style.pointerEvents = "none";
    el.scrollTo({ top: scrollTop, behavior: "smooth" });

    let rafId: number;
    const poll = () => {
      if (Math.round(el.scrollTop) === Math.round(scrollTop)) {
        unlock();
      } else {
        rafId = requestAnimationFrame(poll);
      }
    };
    rafId = requestAnimationFrame(poll);
    return () => { cancelAnimationFrame(rafId); };
  }, [selectedSpice]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { ref, onScroll, displayed };
}

export function Quantity({ selectedSpice, min = 0, max = 100, step = 1, throttle = 20 }: QuantityProps) {
  const [quantity] = useFilterQuantity();
  const committed = Math.round(quantity);
  const { ref, onScroll, displayed } = useQuantityScroll(selectedSpice, committed, min, max, step, throttle);

  const values = useMemo(
    () => Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => min + i * step),
    [min, max, step]
  );

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="relative flex-1 min-w-0">
        {/* Highlight bar centred over the active row */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-8  border-2 border-secondary rounded-sm pointer-events-none" />

        <div
          ref={ref}
          onScroll={onScroll}
          className="flex flex-col overflow-y-auto h-20 relative w-full"
          style={{ scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch" }}
        >
          {values.map((n) => (
            <div
              key={n}
              className={`shrink-0 h-8 flex items-center justify-center font-bold text-lg tracking-tight pointer-events-none select-none w-full transition-colors ${
                n === displayed ? "text-secondary" : "text-secondary/30"
              }`}
              style={{ scrollSnapAlign: "center" }}
            >
              {n}
            </div>
          ))}
        </div>
      </div>

      <Units className="border-0 bg-transparent px-0 text-xs font-medium text-secondary shrink-0" disabled={false} />
    </div>
  );
}
